import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { getStoredUtmParams } from '@/lib/utm';
import { trackEvent } from '@/lib/analytics';
import { trackGoogleAdsConversion } from '@/lib/google-ads';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    document: '',
    whatsapp: '',
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Read referral cookie for affiliate attribution
      const refCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_affiliate='));
      const referred_by = refCookie ? refCookie.split('=')[1].trim() : undefined;

      // Read owner referral cookie (Indique e Ganhe)
      const refOwnerCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_owner='));
      const referred_by_owner = refOwnerCookie ? refOwnerCookie.split('=')[1].trim() : undefined;

      // Read partner referral cookie (parceiro tipo TecnoWeb)
      const refPartnerCookie = document.cookie.split(';').find(c => c.trim().startsWith('ref_partner='));
      const referred_by_partner = refPartnerCookie ? refPartnerCookie.split('=')[1].trim() : undefined;

      // UTM attribution (campanhas)
      const utm = getStoredUtmParams();

      await register({ ...formData, referred_by, referred_by_owner, referred_by_partner, ...utm });
      trackEvent('signup_completed', {
        method: 'email',
        has_referral: !!(referred_by || referred_by_owner || referred_by_partner),
        utm_source: utm?.utm_source || null,
      });
      trackGoogleAdsConversion('signup');
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4" data-testid="register-page">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="flex flex-col items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Falo Com O Dono" className="h-32 w-auto object-contain" />
            <span className="font-semibold text-xl text-[#0A2647]">Falo Com O Dono</span>
          </Link>
          <CardTitle className="text-xl font-semibold text-slate-900">Criar Conta</CardTitle>
          <CardDescription>Comece a receber avaliações dos seus clientes</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Seu Nome</Label>
              <Input
                id="name"
                name="name"
                placeholder="João Silva"
                value={formData.name}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">E-mail</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="h-11"
                data-testid="register-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium">Nome da Empresa</Label>
              <Input
                id="company_name"
                name="company_name"
                placeholder="Padaria do João"
                value={formData.company_name}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="register-company-input"
              />
              <p className="text-[11px] text-slate-500">Você completa os outros dados depois, em minutos.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="document" className="text-sm font-medium">CPF ou CNPJ</Label>
              <Input
                id="document"
                name="document"
                inputMode="numeric"
                placeholder="Somente números"
                value={formData.document}
                onChange={handleChange}
                required
                className="h-11"
                data-testid="register-document-input"
              />
              <p className="text-[11px] text-slate-500">
                São 30 dias grátis no plano Empresarial. Usamos o documento só para o boleto que vence em 30 dias — cancele antes e não paga nada.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="text-sm font-medium">
                Seu WhatsApp <span className="text-xs text-slate-400 font-normal">(opcional)</span>
              </Label>
              <Input
                id="whatsapp"
                name="whatsapp"
                type="tel"
                placeholder="(11) 91234-5678"
                value={formData.whatsapp}
                onChange={handleChange}
                className="h-11"
                data-testid="register-whatsapp-input"
                autoComplete="tel"
              />
              <p className="text-[11px] text-slate-500">
                Te avisamos por WhatsApp quando um cliente reclamar — para você resolver na hora.
              </p>
            </div>
            <Button 
              type="submit" 
              className="w-full btn-secondary h-11"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Criando conta...</>
              ) : (
                'Criar Conta Grátis'
              )}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-600">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-[#0A2647] font-semibold hover:underline" data-testid="login-link">
              Fazer login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
